import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { suggestNiceToHaves } from "@/services/v2/wantAdService";

const DEBOUNCE_MS = 1200;
export const MIN_TEXT_CHARS = 15;

// AI nice-to-have suggestions for a want-ad draft's title/description.
// Keystrokes settle for DEBOUNCE_MS before becoming the query key, so React
// Query only fetches once per pause - and retyping earlier text is served from
// its cache without a request (the backend hashes normalised text too, so even
// a cache miss here is usually its cache hit). Fails open to an empty list.
export default function useN2hSuggestions(title, description, active) {
  const [settled, setSettled] = useState({ title: "", description: "" });

  useEffect(() => {
    const handle = setTimeout(
      () => setSettled({ title: title || "", description: description || "" }),
      DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [title, description]);

  const textLength = (settled.title.trim() + settled.description.trim())
    .length;
  const enabled = !!active && textLength >= MIN_TEXT_CHARS;

  const { data, isFetching } = useQuery({
    queryKey: ["n2hSuggestions", settled.title, settled.description],
    queryFn: () =>
      suggestNiceToHaves({
        title: settled.title,
        description: settled.description,
        wantAdType: "buy",
      }),
    enabled,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: false, // the backend fails soft; never hammer a refusal
    // Hold the previous verdict while the next one loads, so the panel keeps
    // its chips instead of emptying and refilling on every edit.
    placeholderData: (previous) => previous,
  });

  // True while keystrokes are still settling: the live text differs from the
  // text the current query answered. The panel treats this as busy so it
  // never blinks out between typing and the fetch starting.
  const debouncing =
    (title || "") !== settled.title || (description || "") !== settled.description;

  if (!enabled) {
    return { suggestions: [], loading: false, answered: false, debouncing };
  }
  // answered distinguishes a real (possibly empty) server verdict for the
  // CURRENT settled text from "not fetched yet" or a failed request — callers
  // may un-tick earlier auto-adds only on a real verdict.
  return {
    suggestions: data?.result?.suggestions || [],
    loading: isFetching,
    answered: data !== undefined && !isFetching,
    debouncing,
  };
}
